import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaFacebookF, FaLinkedinIn, FaYoutube, FaInstagram, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { getLocalities, getTopDevelopers, getContactOffices } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const Footer = () => {
  const [localities, setLocalities] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [offices, setOffices] = useState([]);
  const [footerSettings, setFooterSettings] = useState(null);

  useEffect(() => {
    // Fetch footer settings
    axios.get(`${API_BASE_URL}/footer-settings`)
      .then(res => {
        if (res.data.success) {
          setFooterSettings(res.data.settings);
        }
      })
      .catch(err => console.error('Error fetching footer settings:', err));

    // Fetch localities from backend
    getLocalities().then(data => {
      // Extract locality objects with slugs from the backend data structure
      if (typeof data === 'object' && !Array.isArray(data)) {
        // If data is an object with keys as categories (cities)
        const allLocalities = [];
        Object.entries(data).forEach(([city, categoryData]) => {
          if (Array.isArray(categoryData)) {
            categoryData.forEach(loc => {
              if (loc.name && loc.slug) {
                // Store full object with city info
                allLocalities.push({ ...loc, city });
              }
            });
          }
        });
        setLocalities(allLocalities.slice(0, 4)); // Limit to 4 for footer
      } else if (Array.isArray(data)) {
        setLocalities(data.slice(0, 4));
      }
    });

    // Fetch builders from backend
    getTopDevelopers().then(data => {
      if (Array.isArray(data)) {
        // Store full objects with slug
        setBuilders(data.slice(0, 4));
      }
    });

    // Fetch offices from backend
    getContactOffices().then(data => {
      if (Array.isArray(data)) {
        setOffices(data);
      }
    });
  }, []);
  return (
    <footer id="contact" className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <img 
                src={footerSettings?.logoUrl || "/mainlogo.png"} 
                alt={footerSettings?.companyName || "MetroBrix"} 
                className="h-12 w-auto" 
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {footerSettings?.reraNumber && (
                <>RERA No. - {footerSettings.reraNumber}<br /></>
              )}
              {footerSettings?.cinNumber && (
                <>C.I. Number: {footerSettings.cinNumber}</>
              )}
            </p>
            <div className="flex items-center space-x-2 text-gray-400 text-sm mb-2">
              <FaEnvelope className="text-red-400" />
              <a 
                href={`mailto:${footerSettings?.supportEmail || 'support@metrobrix.com'}`} 
                className="hover:text-white transition"
              >
                {footerSettings?.supportEmail || 'support@metrobrix.com'}
              </a>
            </div>

            {/* Social */}
            <div className="flex items-center space-x-3 mt-6">
              {footerSettings?.socialLinks?.facebook && (
                <a
                  href={footerSettings.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition"
                >
                  <FaFacebookF />
                </a>
              )}
              {footerSettings?.socialLinks?.linkedin && (
                <a
                  href={footerSettings.socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-700 transition"
                >
                  <FaLinkedinIn />
                </a>
              )}
              {footerSettings?.socialLinks?.youtube && (
                <a
                  href={footerSettings.socialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition"
                >
                  <FaYoutube />
                </a>
              )}
              {footerSettings?.socialLinks?.instagram && (
                <a
                  href={footerSettings.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-pink-600 transition"
                >
                  <FaInstagram />
                </a>
              )}
            </div>
          </div>

          {/* Top Localities */}
          <div>
            <h3 className="font-bold text-lg mb-4">Top Localities</h3>
            {localities.length > 0 ? (
              <ul className="space-y-2.5">
                {localities.map((loc, idx) => (
                  <li key={idx}>
                    <Link to={`/in/${(loc.city || 'mumbai').toLowerCase()}/${loc.slug}`} className="text-gray-400 text-sm hover:text-red-400 transition flex items-center space-x-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Top Projects in {loc.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">Loading...</p>
            )}
          </div>

          {/* Top Builders */}
          <div>
            <h3 className="font-bold text-lg mb-4">Top Builders</h3>
            {builders.length > 0 ? (
              <ul className="space-y-2.5">
                {builders.map((builder, idx) => (
                  <li key={idx}>
                    <Link to={`/builder/${builder.slug}`} className="text-gray-400 text-sm hover:text-red-400 transition flex items-center space-x-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      <span>Top Projects by {builder.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">Loading...</p>
            )}
          </div>

          {/* Links & Offices */}
          <div>
            <h3 className="font-bold text-lg mb-4">Useful Links</h3>
            <ul className="space-y-2.5 mb-8">
              {footerSettings?.usefulLinks && footerSettings.usefulLinks.length > 0 ? (
                footerSettings.usefulLinks.map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.url} className="text-gray-400 text-sm hover:text-red-400 transition">
                      {link.label}
                    </Link>
                  </li>
                ))
              ) : (
                <div>
                  <li><Link to="/calculators" className="text-gray-400 text-sm hover:text-red-400 transition">EMI Calculator</Link></li>
                  <li><Link to="/contact" className="text-gray-400 text-sm hover:text-red-400 transition">Contact Us</Link></li>
                  <li><Link to="/about" className="text-gray-400 text-sm hover:text-red-400 transition">About us</Link></li>
                  <li><Link to="/privacy-policy" className="text-gray-400 text-sm hover:text-red-400 transition">Privacy policy</Link></li>
                  <li><Link to="/terms-conditions" className="text-gray-400 text-sm hover:text-red-400 transition">Term & conditions</Link></li>
                </div>
              )}
            </ul>

          </div>
        </div>

        {/* Offices */}
        <div className="mt-14 pt-10 border-t border-gray-800">
          <h3 className="font-bold text-lg mb-6">Our Offices</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {offices.map((office, idx) => (
              <div key={idx} className="flex items-start space-x-3 bg-gray-800/50 rounded-xl p-4">
                <FaMapMarkerAlt className="text-red-400 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-sm mb-1">{office.city}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{office.address}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between">
          <p className="text-gray-500 text-xs">
            {footerSettings?.copyrightText || '2026 © MetroBrix'}
          </p>
          
        </div>
      </div>
    </footer>
  );
};

export default Footer;
